import { SettingFilled } from '@ant-design/icons';
import { Checkbox, Flex, Form, Slider, Switch } from 'antd';
import { useRef, useState } from 'react';

import { HubSettings } from '@suntex.fim/hub';
import { Button, Popover, useTheme } from '@suntex.fim/ui';

export const ANIMATION_SPEED_MIN = 0.5;
export const ANIMATION_SPEED_MAX = 2;

export interface HubSettingsMenuProps {
  settings: HubSettings;
  applySettings: (settings: HubSettings) => void;
  /** Values restored by the "Reset defaults" button. */
  defaultSettings: HubSettings;
}

/**
 * Settings menu component for Hub animation controls
 */
export function HubSettingsMenu({
  settings,
  applySettings,
  defaultSettings,
}: HubSettingsMenuProps) {
  const { colors } = useTheme();
  const searchableCatalogRef = useRef<HTMLButtonElement>(null);
  const [disableAnimation, setDisableAnimation] = useState(
    settings.disableAnimation ?? defaultSettings.disableAnimation,
  );
  const [animationSpeed, setAnimationSpeed] = useState(
    settings.animationSpeed ?? defaultSettings.animationSpeed,
  );
  const [flatten, setFlatten] = useState(
    settings.flatten ?? defaultSettings.flatten,
  );
  const [alwaysShowLabels, setAlwaysShowLabels] = useState(
    settings.alwaysShowLabels ?? defaultSettings.alwaysShowLabels,
  );
  const [catalogView, setCatalogView] = useState(
    settings.catalogView ?? defaultSettings.catalogView,
  );
  const [defaultToAllModuleView, setDefaultToAllModuleView] = useState(
    settings.defaultPath === '/modules',
  );

  const hasCustomValues =
    alwaysShowLabels !== defaultSettings.alwaysShowLabels ||
    animationSpeed !== defaultSettings.animationSpeed ||
    catalogView !== defaultSettings.catalogView ||
    defaultToAllModuleView !== (defaultSettings.defaultPath === '/modules') ||
    disableAnimation !== defaultSettings.disableAnimation ||
    flatten !== defaultSettings.flatten;

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    animationSpeed !== settings.animationSpeed ||
    alwaysShowLabels !== settings.alwaysShowLabels ||
    catalogView !== settings.catalogView ||
    defaultToAllModuleView !== (settings.defaultPath === '/modules') ||
    disableAnimation !== settings.disableAnimation ||
    flatten !== settings.flatten;

  const resetToDefaults = () => {
    setDisableAnimation(defaultSettings.disableAnimation);
    setAnimationSpeed(defaultSettings.animationSpeed);
    setFlatten(defaultSettings.flatten);
    setAlwaysShowLabels(defaultSettings.alwaysShowLabels);
    setCatalogView(defaultSettings.catalogView);
    setDefaultToAllModuleView(defaultSettings.defaultPath === '/modules');
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          requestAnimationFrame(() => searchableCatalogRef.current?.focus());
        }
      }}
      placement="bottomLeft"
      styles={{
        body: {
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
        },
      }}
      trigger="click"
      content={
        <Form
          aria-label="Hub settings"
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '16px',
            minWidth: '250px',
          }}
        >
          <Flex vertical gap={16}>
            <h5>Hub Settings</h5>

            <Checkbox
              checked={defaultToAllModuleView}
              onChange={(e) => setDefaultToAllModuleView(e.target.checked)}
            >
              Show all modules to start
            </Checkbox>

            <Flex align="center" justify="space-between">
              <span>Searchable tree view</span>
              <Switch
                aria-label="Searchable tree view"
                checked={catalogView}
                onChange={(checked) => setCatalogView(checked)}
                ref={searchableCatalogRef}
              />
            </Flex>

            <Checkbox
              checked={flatten}
              disabled={catalogView}
              onChange={(e) => setFlatten(e.target.checked)}
            >
              Flatten graphics
            </Checkbox>

            <Checkbox
              checked={alwaysShowLabels}
              disabled={catalogView}
              onChange={(e) => setAlwaysShowLabels(e.target.checked)}
            >
              Always show labels
            </Checkbox>

            <Checkbox
              checked={disableAnimation}
              disabled={catalogView}
              onChange={(e) => setDisableAnimation(e.target.checked)}
            >
              Disable hub animations
            </Checkbox>

            <div>
              <div
                style={{
                  marginBottom: '8px',
                  fontWeight: 500,
                }}
              >
                Animation speed:{' '}
                {disableAnimation
                  ? 0
                  : animationSpeed
                    ? animationSpeed.toFixed(1)
                    : 1}
                x
              </div>
              <Slider
                ariaLabelForHandle="Animation speed"
                min={ANIMATION_SPEED_MIN}
                max={ANIMATION_SPEED_MAX}
                step={0.1}
                value={animationSpeed}
                disabled={disableAnimation || catalogView}
                onChange={(value) => setAnimationSpeed(value)}
                marks={{
                  [ANIMATION_SPEED_MIN]: `${ANIMATION_SPEED_MIN}x`,
                  1: '1x',
                  1.5: '1.5x',
                  [ANIMATION_SPEED_MAX]: `${ANIMATION_SPEED_MAX}x`,
                }}
              />
            </div>
            <Button
              intent={'primary'}
              onClick={() => {
                applySettings({
                  alwaysShowLabels,
                  animationSpeed,
                  disableAnimation,
                  flatten,
                  catalogView,
                  defaultPath: defaultToAllModuleView
                    ? '/modules'
                    : defaultSettings.defaultPath,
                });
              }}
              disabled={!hasUnsavedChanges}
            >
              Apply Settings
            </Button>

            <Button
              intent="text"
              size="small"
              style={{ flex: 1 }}
              onClick={resetToDefaults}
              disabled={!hasCustomValues}
            >
              Reset
            </Button>
          </Flex>
        </Form>
      }
    >
      <Button
        aria-label="Settings"
        shape="round"
        intent="icon"
        icon={<SettingFilled />}
        style={{ color: colors.icon }}
      />
    </Popover>
  );
}
